import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {AccountService} from './account.service';

/*
    "ROLE_USER": "Utilisateur / Candidat",
    "ROLE_SYSTEM": "Administrateur SI",
    "ROLE_ADMIN": "Administrateur fonctionnel",
    "ROLE_GEST": "Gestionnaire",
    "ROLE_REFERENT": "Responsable de formation",
    "ROLE_DELEGATE": "Enseignant",
*/

@Injectable()
export class Principal {

    private userIdentity: any;
    private authenticated = false;
    private authenticationState = new Subject<any>();
    private backOfficeAuthorities = ['ROLE_ADMIN', 'ROLE_SYSTEM', 'ROLE_GEST', 'ROLE_REFERENT', 'ROLE_DELEGATE'];

    static isAuthorityInListe(authorityAVerifier: string, authoritiesListe?: String[]): boolean {
        return authorityAVerifier != null && authoritiesListe && authoritiesListe.indexOf(authorityAVerifier) !== -1;
    }

    static isAnyAuthorityInListe(authorityAVerifier: String[], authoritiesListe?: String[]): boolean {
        for (let i = 0; i < authoritiesListe.length; i++) {
            if (authorityAVerifier.indexOf(authoritiesListe[i]) !== -1) {
                return true;
            }
        }
        return false;
    }

    static isAdmin(authorityAVerifier: String[]): boolean {
        return Principal.isAnyAuthorityInListe(authorityAVerifier, ['ROLE_ADMIN']);
    }

    static isAdminOrGestionnaire(authorityAVerifier: String[]): boolean {
        return Principal.isAnyAuthorityInListe(authorityAVerifier, ['ROLE_ADMIN', 'ROLE_GEST']);
    }

    static isAdminOrGestionnaireOrSystem(authorityAVerifier: String[]): boolean {
        return Principal.isAnyAuthorityInListe(authorityAVerifier, ['ROLE_ADMIN', 'ROLE_SYSTEM', 'ROLE_GEST']);
    }

    static isGestionnaire(authorityAVerifier: String[]): boolean {
        return Principal.isAnyAuthorityInListe(authorityAVerifier, ['ROLE_GEST']);
    }

    static isResponsableFormation(authorityAVerifier: String[]): boolean {
        return Principal.isAnyAuthorityInListe(['ROLE_REFERENT'], authorityAVerifier);
    }

    static isOnlyResponsableFormation(authorityAVerifier: String[]): boolean {
        if (Principal.isAdminOrGestionnaire(authorityAVerifier)) {
            return false;
        } else {
            return Principal.isAnyAuthorityInListe(['ROLE_REFERENT'], authorityAVerifier);
        }
    }

    static isOnlyEnseignant(authorityAVerifier: String[]): boolean {
        if (Principal.isAdminOrGestionnaire(authorityAVerifier)) {
            return false;
        } else {
            return Principal.isAnyAuthorityInListe(['ROLE_DELEGATE'], authorityAVerifier);
        }
    }


    constructor(private account: AccountService) {
    }

    authenticate(identity) {
        this.userIdentity = identity;
        this.authenticated = identity !== null;
        this.authenticationState.next(this.userIdentity);
    }

    hasAnyAuthority(authorities: string[]): Promise<boolean> {
        if (!this.authenticated || !this.userIdentity || !this.userIdentity.authorities) {
            return Promise.resolve(false);
        }
        for (let i = 0; i < authorities.length; i++) {
            if (this.userIdentity.authorities.indexOf(authorities[i]) !== -1) {
                return Promise.resolve(true);
            }
        }
        return Promise.resolve(false);
    }

    hasAuthority(authorityRechercher: string): Promise<boolean> {
        if (!this.authenticated) {
            return Promise.resolve(false);
        }
        return this.identity().then(user => {
            return Promise.resolve(Principal.isAuthorityInListe(authorityRechercher, user.authorities));
        }, () => {
            return Promise.resolve(false);
        });
    }

    rootUrl(): Promise<String> {
        if (this.authenticated) {
            return this.hasAnyAuthority(this.backOfficeAuthorities).then(result => {
                if (result) {
                    return '/candidature';
                } else {
                    return '/espaceCandidat';
                }
            });
        } else {
            return Promise.resolve('/login');
        }
    }

    getId(): number {
        if (this.userIdentity) {
            return this.userIdentity.id;
        } else {
            return this.userIdentity.id;
        }
    }


    getIdentityName(): Promise<String> {
        if (this.userIdentity) {
            return Promise.resolve(this.userIdentity.firstName + ' ' + this.userIdentity.lastName.toLocaleUpperCase());
        } else {
            return this.account.get().toPromise().then(account => {
                if (account) {
                    return account.firstName + ' ' + account.lastName.toLocaleUpperCase();
                } else {
                    return '';
                }
            });
        }
    }

    getIdentityLogin(): Promise<String> {
        if (this.userIdentity) {
            return Promise.resolve(this.userIdentity.login);
        } else {
            return this.account.get().toPromise().then(account => {
                if (account) {
                    return account.login;
                } else {
                    return '';
                }
            });
        }
    }

    identity(force?: boolean): Promise<any> {
        if (force === true) {
            this.userIdentity = undefined;
        }

        // check and see if we have retrieved the userIdentity data from the server.
        // if we have, reuse it by immediately resolving
        if (this.userIdentity) {
            return Promise.resolve(this.userIdentity);
        }

        // retrieve the userIdentity data from the server, update the identity object, and then resolve.
        return this.account.get().toPromise().then(account => {
            if (account) {
                this.userIdentity = account;
                this.authenticated = true;
            } else {
                this.userIdentity = null;
                this.authenticated = false;
            }
            this.authenticationState.next(this.userIdentity);
            return this.userIdentity;
        }).catch(() => {
            this.userIdentity = null;
            this.authenticated = false;
            this.authenticationState.next(this.userIdentity);
            return null;
        });
    }


    isAuthenticated(): boolean {
        return this.authenticated;
    }

    isIdentityResolved(): boolean {
        return this.userIdentity !== undefined;
    }

    getAuthenticationState(): Observable<any> {
        return this.authenticationState.asObservable();
    }

    getImageUrl(): String {
        return this.isIdentityResolved() ? this.userIdentity.imageUrl : null;
    }


    getAuthorities(): Promise<String[]> {
        return this.userIdentity.then(user => {
            return user.authorities;
        });
    }


}



// WEBPACK FOOTER //
// ./src/main/webapp/app/shared/auth/principal.service.ts